/**
 * App Component
 *
 * Main application entry point with routing.
 * Handles authentication state and routes between login and authenticated views.
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { getCobrandName, useRuntimeConfigStore } from '@/stores/runtimeConfigStore';
import { LoginPage } from '@/components/LoginPage';
import { Layout } from '@/components/Layout';
import { UserGuide } from '@/components/UserGuide';
import { BrowserTraceBridge } from '@/components/browser-traces';
import { Loader2 } from 'lucide-react';
import { Toaster } from 'sonner';
import { BrandLogo } from '@/components/ui/BrandLogo';

// ============================================
// TelcoOS Logo Component
// ============================================

function TelcoOSLogo({ className }: { className?: string }) {
  return <BrandLogo className={className} />;
}

// ============================================
// Loading Screen
// ============================================

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950">
      <div className="flex flex-col items-center gap-4">
        <TelcoOSLogo className="w-16 h-16 rounded-2xl" />
        <div className="flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
          <span className="text-surface-600 dark:text-surface-400">
            Loading TelcoOS...
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Authenticated Routes Component
// ============================================

function AuthenticatedRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Layout />} />
      <Route path="/users-guide" element={<UserGuide />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ============================================
// Main App Component
// ============================================

function App() {
  const { isAuthenticated, initialized, checkStatus, handleAuthExpired } = useAuthStore();
  const {
    config: runtimeConfig,
    initialized: runtimeConfigInitialized,
    loadRuntimeConfig,
  } = useRuntimeConfigStore();

  useEffect(() => {
    loadRuntimeConfig();
  }, [loadRuntimeConfig]);

  // Check auth status on mount
  useEffect(() => {
    if (!initialized) {
      checkStatus();
    }
  }, [initialized, checkStatus]);

  useEffect(() => {
    window.addEventListener('telcoos-auth-expired', handleAuthExpired);
    return () => {
      window.removeEventListener('telcoos-auth-expired', handleAuthExpired);
    };
  }, [handleAuthExpired]);

  useEffect(() => {
    document.title = runtimeConfigInitialized ? getCobrandName(runtimeConfig) : 'TelcoOS';
  }, [runtimeConfig, runtimeConfigInitialized]);

  // Show loading screen while checking auth status
  if (!initialized || !runtimeConfigInitialized) {
    return (
      <>
        <LoadingScreen />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<LoginPage />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    );
  }

  // Show authenticated app with routing
  return (
    <BrowserRouter>
      <BrowserTraceBridge />
      <AuthenticatedRoutes />
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );
}

export default App;
